import Currency from 'app/shared-components/util/Currency';
import { useTranslation } from 'react-i18next';
import { ReimbursementTransaction, ReimbursementTransactionStatus } from 'API';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { formatTransactionType } from 'app/pages/overview/tabs/reimbursements/utils';

interface MobileReimbursementItemProps {
  reimbursement: ReimbursementTransaction;
  onReimbursementClick: (reimbursementId: string) => void;
  status?: ReimbursementTransactionStatus;
}

const MobileReimbursementItem = ({
  reimbursement,
  onReimbursementClick,
  status,
}: MobileReimbursementItemProps) => {
  const { t } = useTranslation();

  // Show decisionDate for approved/denied, createdAt for requested
  const displayDate =
    status === ReimbursementTransactionStatus.APPROVED || status === ReimbursementTransactionStatus.DENIED
      ? reimbursement.decisionDate
      : reimbursement.createdAt;

  // Mirrors the mobile transactions row: two rows, size+color for hierarchy (not bold).
  const topTextSx = {
    fontSize: '1.1rem',
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: '0.5px',
    color: 'neutral50.main',
  };
  const bottomTextSx = {
    fontSize: '1.6rem',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: '#3b3b3e',
  };

  return (
    <Card
      elevation={0}
      onClick={() => reimbursement.id && onReimbursementClick(reimbursement.id)}
      sx={{
        cursor: 'pointer',
        borderRadius: 0,
        bgcolor: 'transparent',
        position: 'relative',
        // Inset divider (doesn't run edge-to-edge), hidden on the last row.
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          left: 16,
          right: 16,
          bottom: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <CardContent sx={{ p: 2, pb: '16px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pb: 0.5 }}>
          <Typography sx={topTextSx}>
            {displayDate ? new Date(displayDate).toLocaleDateString() : 'N/A'}
          </Typography>
          <Typography sx={{ ...topTextSx, mr: 3 }}>
            {t(formatTransactionType(reimbursement.transactionType))}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography sx={bottomTextSx}>
            <Currency
              value={reimbursement?.amount?.value || 0}
              currency={reimbursement?.amount?.currencyCode || 'USD'}
            />
          </Typography>
          <Typography sx={{ ...bottomTextSx, mr: 3 }}>{reimbursement.merchantName || 'N/A'}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MobileReimbursementItem;
